import type {
  ApiSuccessResponse,
  ListWithPaginationAndSearchReq,
  PaginatedData,
  PaginatedMeta,
  Receipt,
  ReceiptRule,
  ReceiptRuleV2,
  ReceiptRuleV2CreateRequest,
  ReceiptRuleV2UpdateRequest,
} from '../../common/types';
import { apiSlice } from '../../app/services/api-slice';
import {type OwnerType} from "../../common/constants";
import { urlWithPageAndTerm } from '../../common/helpers';

const tagName: string = 'Receipt';

export const receiptApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: [tagName]
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAllReceipts: builder.query<PaginatedData<Receipt> | null, ListWithPaginationAndSearchReq>({
        query: ({ term = '', page = 1 }): string => {
          return urlWithPageAndTerm('parse-receipt', { term, page });
        },
        transformResponse: (data: ApiSuccessResponse) => {
          if (data.data === undefined) {
            return null;
          }
          return {
            data: data.data.data as Receipt[],
            meta: data.data.meta as PaginatedMeta
          };
        },
        providesTags: (result) =>
          result != null
            ? [
                ...result.data.map(({ id }) => ({
                  type: tagName,
                  id
                })),
                { type: tagName, id: 'LIST' }
              ]
            : [{ type: tagName, id: 'LIST' }]
      }),
      getReceiptRuleById: builder.query<ReceiptRule, string>({
        query: (ruleId) => `/receipts/rule/${ruleId}`,
        transformResponse: (data: ApiSuccessResponse) => {
          return data.data as ReceiptRule;
        },
        providesTags: (result) => (result != null ? [{ type: tagName, id: result?.id }] : [])
      }),
      getReceiptRuleByOwner: builder.query<ReceiptRule, { ownerEntityType: string; ownerEntityId: string }>({
        query: ({ ownerEntityType, ownerEntityId }) => `/receipts/rule/${ownerEntityType}/${ownerEntityId}`,
        transformResponse: (data: ApiSuccessResponse) => {
          return data.data as ReceiptRule;
        },
        providesTags: (result) => (result != null ? [{ type: tagName, id: result?.id }] : [])
      }),
      updateRule: builder.mutation<ReceiptRuleV2, ReceiptRuleV2UpdateRequest>({
        query: ({ id, body }) => ({
          url: `/receipts/rule/${id}`,
          method: 'PATCH',
          body
        }),
        transformResponse: (data: ApiSuccessResponse) => {
          return data.data as ReceiptRuleV2;
        }
      }),
      addRule: builder.mutation<ReceiptRuleV2, ReceiptRuleV2CreateRequest>({
        query: (body) => ({
          url: `/receipts/rule`,
          method: 'POST',
          body
        }),
        transformResponse: (data: ApiSuccessResponse) => {
          return data.data as ReceiptRuleV2;
        }
      }),
      getRuleByOwner: builder.query<ReceiptRule, { ownerEntityType: OwnerType; ownerEntityId: string }>({
        query: ({ ownerEntityType, ownerEntityId }) => `receipts/rule/${ownerEntityType}/${ownerEntityId}`,
        transformResponse: (data: ApiSuccessResponse) => {
          return data.data as ReceiptRule;
        }
      }),
      testReceiptRule: builder.mutation({
        query: (data: { ruleId: string; sampleReceiptIds: string[] }) => ({
          url: '/sample-receipts/test',
          method: 'POST',
          body: data
        }),
        transformResponse: (data: ApiSuccessResponse) => {
          return data.data;
        }
      }),
      deleteReceiptRuleById: builder.mutation<void, string>({
        query: (id: string) => ({
          url: `/receipts/rule/${id}`,
          method: 'DELETE'
        }),
        invalidatesTags: (result, error, arg) => [{ type: tagName, id: 'LIST' }]
      }),
      deleteReceiptById: builder.mutation<void, string>({
        query: (id: string) => ({
          url: `/parse-receipt/${id}`,
          method: 'DELETE'
        }),
        invalidatesTags: (result, error, arg) => [{ type: tagName, id: 'LIST' }]
      })
    })
  });

export const {
  useGetAllReceiptsQuery,
  useGetReceiptRuleByIdQuery,
  useGetReceiptRuleByOwnerQuery,
    useGetRuleByOwnerQuery,
  useUpdateRuleMutation,
  useAddRuleMutation,
  useTestReceiptRuleMutation,
  useDeleteReceiptByIdMutation,
  useDeleteReceiptRuleByIdMutation
} = receiptApi;
export default receiptApi;
